import { Widget } from 'src/app/core/models/core.models';
import { WidgetType } from 'src/app/core/constants';

/**
 * Every widget-list consumer (feed, dashboard, presentation-feed) applies these
 * transforms over the list query's data, in this order: sort, staged layout,
 * search filter.
 */

/** Un-persisted geometry for one widget, held while a drag/resize settles. */
export interface StagedWidgetLayout {
  x: number;
  y: number;
  cols: number;
  rows: number;
}

/** Reading order: top-to-bottom, then left-to-right. */
export function sortWidgetsByPosition(widgets: Widget[]): Widget[] {
  return [...widgets].sort((a, b) => {
    const ay = a.y ?? 0,
      by = b.y ?? 0;
    if (ay !== by) return ay - by;
    const ax = a.x ?? 0,
      bx = b.x ?? 0;
    return ax - bx;
  });
}

/**
 * The title always matches; the export title only in widget edit mode, which is
 * where the list shows both.
 */
export function filterWidgetsBySearchString(
  widgets: Widget[],
  searchString: string,
  editMode: boolean,
  languageId: number,
): Widget[] {
  const query = searchString?.trim().toLowerCase() ?? '';
  if (!query) return widgets;

  return widgets.filter((widget) => {
    const titleMatch = widget.title?.toLowerCase().includes(query);

    if (!editMode) {
      return titleMatch;
    }

    const exportTitle =
      widget.settings?.export_title?.[`language_id_${languageId}`];

    const exportMatch = exportTitle?.toLowerCase().includes(query);

    return titleMatch || exportMatch;
  });
}

/**
 * Merge the un-persisted layout over the fetched list. A projection rather than a
 * mutation, so a list refetch can never drop a pending drag and can never reach
 * another view's cache entry.
 *
 * **Identity-stable on purpose.** A widget the overlay agrees with is returned
 * *as-is*, and a list nothing differs on is returned as the same array. Two things
 * depend on that: gridstack-item's `[options]` must not churn (a new reference
 * makes it call `grid.update()`, which fires `changeCB`, which stages and PATCHes
 * again), and `widget-card`'s `@Input()` must not churn (`ngOnChanges` runs
 * `detectChanges` on every card). It is also what lets the flush skip a request
 * whose geometry the cache already holds - see `WidgetLayoutStagingService`.
 */
export function applyStagedLayout(
  widgets: Widget[],
  staged: ReadonlyMap<string, StagedWidgetLayout>,
): Widget[] {
  if (!staged.size) return widgets;

  let changed = false;

  const merged = widgets.map((widget) => {
    const layout = staged.get(widget.id);
    if (!layout || matchesStagedLayout(widget, layout)) return widget;

    changed = true;

    return {
      ...widget,
      x: layout.x,
      y: layout.y,
      cols: layout.cols,
      rows: layout.rows,
      sizeX: layout.cols,
      sizeY: layout.rows,
      row: layout.y,
      col: layout.x,
    };
  });

  return changed ? merged : widgets;
}

/** Does this widget already sit exactly where the overlay says it should? */
function matchesStagedLayout(
  widget: Widget,
  layout: StagedWidgetLayout,
): boolean {
  return (
    widget.x === layout.x &&
    widget.y === layout.y &&
    widget.cols === layout.cols &&
    widget.rows === layout.rows &&
    widget.sizeX === layout.cols &&
    widget.sizeY === layout.rows &&
    widget.row === layout.y &&
    widget.col === layout.x
  );
}

/**
 * The lowest 1-based row no real widget occupies. Placeholders don't count - a new
 * widget may land on one.
 */
export function firstAvailableWidgetRow(widgets: Widget[]): number {
  const occupiedRows = new Set<number>();

  widgets.forEach((widget) => {
    if (widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER) {
      for (let i = widget.row; i < widget.row + widget.rows; i++) {
        occupiedRows.add(i);
      }
    }
  });

  let rowNumber = 1;
  while (occupiedRows.has(rowNumber)) {
    rowNumber++;
  }

  return rowNumber;
}
